// App.js
// Main app file — handles auth, data, and all UI

import { useState, useEffect, useRef } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";
import { auth, googleProvider, db } from "./firebase";

// ─────────────────────────────────────────────
// STATUS CONFIG
// ─────────────────────────────────────────────
const STATUSES = [
  { value: "interested",  label: "Interested",  color: "#1A1A1A", bg: "#F5F500", dot: "#1A1A1A" },
  { value: "visited",     label: "Visited",     color: "#1A1A1A", bg: "#E0E0E0", dot: "#555" },
  { value: "offer_made",  label: "Offer Made",  color: "#FFF",    bg: "#1A1A1A", dot: "#F5F500" },
  { value: "rejected",    label: "Rejected",    color: "#FFF",    bg: "#CC0000", dot: "#FF6B6B" },
  { value: "sold_gone",   label: "Sold / Gone", color: "#999",    bg: "#F0F0F0", dot: "#BBB" },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map(s => [s.value, s]));

const EMPTY_FORM = {
  address: "", price: "", beds: "", baths: "",
  sqft: "", link: "", status: "interested", notes: "", photos: [], rating: 0,
};

// ─────────────────────────────────────────────
// IMAGE COMPRESSION
// ─────────────────────────────────────────────
function compressImage(file, maxWidth = 1000, quality = 0.75) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        if (width > maxWidth) { height = Math.round((height * maxWidth) / width); width = maxWidth; }
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

function formatPrice(price) {
  if (!price) return null;
  const n = parseFloat(String(price).replace(/[^0-9.]/g, ""));
  if (isNaN(n)) return price;
  if (n >= 1000000) return "$" + (n / 1000000).toFixed(2) + "M";
  if (n >= 1000) return "$" + Math.round(n / 1000) + "K";
  return "$" + n.toLocaleString();
}

// ─────────────────────────────────────────────
// GLOBAL CSS
// ─────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;900&family=Bebas+Neue&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #F8F8F8; font-family: 'Inter', system-ui, sans-serif; -webkit-font-smoothing: antialiased; }
  .ht-root { min-height: 100vh; background: #F8F8F8; }

  /* ── AUTH SCREEN ── */
  .ht-auth {
    min-height: 100vh; background: #1A1A1A;
    display: flex; flex-direction: column;
  }
  .ht-auth-hero {
    padding: 56px 28px 36px; flex: 1;
    display: flex; flex-direction: column; justify-content: center;
  }
  .ht-auth-eyebrow {
    font-size: 10px; font-weight: 700; letter-spacing: 3px;
    text-transform: uppercase; color: #F5F500; margin-bottom: 12px;
    animation: htFadeUp 0.5s ease both;
  }
  .ht-auth-headline {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 64px; line-height: 0.92; color: #FFF;
    letter-spacing: 1px; margin-bottom: 16px;
    animation: htFadeUp 0.5s 0.06s ease both;
  }
  .ht-auth-headline em { color: #F5F500; font-style: normal; display: block; }
  .ht-auth-sub {
    font-size: 14px; color: #666; line-height: 1.6;
    animation: htFadeUp 0.5s 0.12s ease both; max-width: 300px;
  }

  .ht-auth-card {
    background: #FFF; border-radius: 24px 24px 0 0;
    padding: 28px 24px 40px;
    animation: htModalUp 0.4s cubic-bezier(0.34, 1.1, 0.64, 1) both;
    animation-delay: 0.2s;
  }
  .ht-auth-tabs {
    display: flex; gap: 0; margin-bottom: 24px;
    border-bottom: 2px solid #F0F0F0;
  }
  .ht-auth-tab {
    flex: 1; padding: 10px 0; background: transparent; border: none;
    font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 700;
    color: #CCC; cursor: pointer; border-bottom: 2.5px solid transparent;
    margin-bottom: -2px; transition: all 0.15s; letter-spacing: 0.3px;
  }
  .ht-auth-tab.active { color: #1A1A1A; border-bottom-color: #F5F500; }

  .ht-auth-label {
    display: block; font-size: 10px; font-weight: 800; color: #AAA;
    letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 5px;
  }
  .ht-auth-input {
    width: 100%; padding: 12px 14px; border: 1.5px solid #EBEBEB;
    border-radius: 10px; font-family: 'Inter', sans-serif; font-size: 15px;
    color: #1A1A1A; background: #FAFAFA; margin-bottom: 12px;
    outline: none; transition: border-color 0.15s; font-weight: 500;
  }
  .ht-auth-input:focus { border-color: #1A1A1A; background: #FFF; }

  .ht-auth-submit {
    width: 100%; padding: 15px; background: #1A1A1A; color: #F5F500;
    border: none; border-radius: 12px; font-family: 'Inter', sans-serif;
    font-size: 13px; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase;
    cursor: pointer; margin-bottom: 12px; transition: all 0.15s;
  }
  .ht-auth-submit:active { transform: scale(0.99); opacity: 0.9; }
  .ht-auth-submit:disabled { opacity: 0.5; cursor: not-allowed; }

  .ht-auth-divider {
    display: flex; align-items: center; gap: 12px; margin-bottom: 12px;
  }
  .ht-auth-divider-line { flex: 1; height: 1px; background: #F0F0F0; }
  .ht-auth-divider-text { font-size: 11px; color: #CCC; font-weight: 600; }

  .ht-google-btn {
    width: 100%; padding: 13px; background: #FFF; color: #1A1A1A;
    border: 1.5px solid #EBEBEB; border-radius: 12px; font-family: 'Inter', sans-serif;
    font-size: 13px; font-weight: 700; cursor: pointer; transition: all 0.15s;
    display: flex; align-items: center; justify-content: center; gap: 10px;
  }
  .ht-google-btn:active { background: #FAFAFA; transform: scale(0.99); }
  .ht-google-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .ht-google-icon { width: 18px; height: 18px; flex-shrink: 0; }

  .ht-auth-error {
    background: #FFF0F0; color: #CC0000; border-radius: 8px;
    padding: 10px 14px; font-size: 12px; font-weight: 600;
    margin-bottom: 12px; border: 1px solid #FFD0D0;
  }

  /* ── HEADER ── */
  .ht-header {
    background: #FFF; border-bottom: 1.5px solid #E8E8E8;
    padding: 0 20px; position: sticky; top: 0; z-index: 100;
    display: flex; align-items: center; justify-content: space-between; height: 56px;
  }
  .ht-logo {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 28px; letter-spacing: 1px; color: #1A1A1A; line-height: 1;
  }
  .ht-logo span {
    display: inline-block; background: #F5F500; color: #1A1A1A;
    padding: 0 4px; margin-left: 1px; border-radius: 3px; font-size: 26px;
  }
  .ht-header-right { display: flex; align-items: center; gap: 10px; }
  .ht-header-add-btn {
    background: #1A1A1A; color: #F5F500; border: none;
    font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 700;
    padding: 8px 14px; border-radius: 8px; cursor: pointer; transition: all 0.15s;
  }
  .ht-header-add-btn:active { transform: scale(0.96); }
  .ht-avatar {
    width: 32px; height: 32px; border-radius: 50%;
    background: #F5F500; display: flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 800; color: #1A1A1A; cursor: pointer;
    border: 2px solid #1A1A1A; flex-shrink: 0; overflow: hidden;
  }
  .ht-avatar img { width: 100%; height: 100%; object-fit: cover; }

  /* ── USER MENU ── */
  .ht-user-menu-overlay {
    position: fixed; inset: 0; z-index: 300;
  }
  .ht-user-menu {
    position: fixed; top: 60px; right: 16px; z-index: 301;
    background: #FFF; border: 1.5px solid #EBEBEB; border-radius: 14px;
    padding: 8px; min-width: 200px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.12);
    animation: htFadeUp 0.18s ease both;
  }
  .ht-user-menu-info {
    padding: 10px 12px 12px; border-bottom: 1px solid #F5F5F5; margin-bottom: 6px;
  }
  .ht-user-menu-name {
    font-size: 13px; font-weight: 700; color: #1A1A1A; margin-bottom: 2px;
  }
  .ht-user-menu-email { font-size: 11px; color: #AAA; }
  .ht-user-menu-btn {
    width: 100%; padding: 10px 12px; background: transparent; border: none;
    font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600;
    color: #CC0000; cursor: pointer; text-align: left; border-radius: 8px;
    transition: background 0.12s;
  }
  .ht-user-menu-btn:hover { background: #FFF5F5; }

  /* ── NAV ── */
  .ht-nav {
    background: #FFF; border-bottom: 1.5px solid #E8E8E8;
    display: flex; padding: 0 20px;
  }
  .ht-nav-btn {
    flex: 1; padding: 12px 8px; background: transparent;
    border: none; border-bottom: 2.5px solid transparent;
    font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 700;
    letter-spacing: 1px; text-transform: uppercase; color: #AAA;
    cursor: pointer; transition: all 0.15s; text-align: center;
  }
  .ht-nav-btn.active { color: #1A1A1A; border-bottom-color: #F5F500; }

  .ht-content { padding: 0 16px 80px; max-width: 640px; margin: 0 auto; }

  /* ── HERO ── */
  .ht-hero {
    background: #1A1A1A; margin: 0 -16px;
    padding: 36px 24px 32px; position: relative; overflow: hidden;
  }
  .ht-hero::before {
    content: ''; position: absolute; top: -40px; right: -40px;
    width: 200px; height: 200px; background: #F5F500;
    border-radius: 50%; opacity: 0.08;
  }
  .ht-hero-eyebrow {
    font-size: 10px; font-weight: 700; letter-spacing: 3px;
    text-transform: uppercase; color: #F5F500; margin-bottom: 10px;
    animation: htFadeUp 0.5s ease both;
  }
  .ht-hero-headline {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 52px; line-height: 0.95; color: #FFF;
    letter-spacing: 1px; margin-bottom: 16px;
    animation: htFadeUp 0.5s 0.08s ease both;
  }
  .ht-hero-headline em { color: #F5F500; font-style: normal; }
  .ht-hero-sub {
    font-size: 13px; color: #888; line-height: 1.6; margin-bottom: 20px;
    animation: htFadeUp 0.5s 0.14s ease both; max-width: 280px;
  }
  .ht-hero-stats { display: flex; gap: 24px; animation: htFadeUp 0.5s 0.2s ease both; }
  .ht-hero-stat-num {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 36px; color: #F5F500; line-height: 1; display: block;
  }
  .ht-hero-stat-lbl {
    font-size: 10px; font-weight: 600; letter-spacing: 1.5px;
    text-transform: uppercase; color: #555; display: block; margin-top: 2px;
  }
  .ht-hero-cta {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    background: #F5F500; color: #1A1A1A; border: none;
    font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 800;
    padding: 14px 18px; border-radius: 10px; cursor: pointer;
    transition: all 0.15s; margin-top: 24px; width: 100%; max-width: 260px;
    animation: htFadeUp 0.5s 0.2s ease both;
  }
  .ht-hero-cta:active { transform: scale(0.97); }
  .ht-hero-cta-arrow { font-size: 18px; flex-shrink: 0; transition: transform 0.15s; }
  .ht-hero-cta:hover .ht-hero-cta-arrow { transform: translateX(4px); }

  @keyframes htFadeUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes htModalUp {
    from { transform: translateY(60px); opacity: 0; }
    to   { transform: translateY(0); opacity: 1; }
  }

  /* ── SECTION ── */
  .ht-section-row {
    display: flex; align-items: center; justify-content: space-between; padding: 20px 0 12px;
  }
  .ht-section-title { font-size: 11px; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; color: #1A1A1A; }
  .ht-section-count { font-size: 11px; font-weight: 600; color: #AAA; }

  /* ── FILTERS ── */
  .ht-filters {
    display: flex; gap: 6px; margin-bottom: 16px;
    overflow-x: auto; padding-bottom: 2px;
    -webkit-overflow-scrolling: touch; scrollbar-width: none;
  }
  .ht-filters::-webkit-scrollbar { display: none; }
  .ht-filter {
    padding: 6px 14px; border-radius: 100px; border: 1.5px solid #E0E0E0;
    background: #FFF; font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 700;
    color: #888; cursor: pointer; white-space: nowrap; flex-shrink: 0; transition: all 0.15s;
  }
  .ht-filter.active { background: #1A1A1A; border-color: #1A1A1A; color: #F5F500; }

  /* ── CARD ── */
  .ht-card {
    background: #FFF; border-radius: 14px; margin-bottom: 10px;
    border: 1.5px solid #EBEBEB; overflow: hidden;
    display: flex; height: 120px; cursor: pointer;
    transition: transform 0.18s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.18s ease;
    animation: htCardIn 0.35s ease both;
  }
  @keyframes htCardIn {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .ht-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.09); }
  .ht-card:active { transform: scale(0.98); box-shadow: none; }
  .ht-card.archived { opacity: 0.5; }

  .ht-card-photos-wrap { position: relative; width: 120px; flex-shrink: 0; }
  .ht-card-photos {
    width: 120px; height: 120px; flex-shrink: 0;
    overflow-x: auto; overflow-y: hidden;
    display: flex; scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch; scrollbar-width: none; background: #1A1A1A;
  }
  .ht-card-photos::-webkit-scrollbar { display: none; }
  .ht-card-photo {
    flex-shrink: 0; width: 120px; height: 120px;
    object-fit: cover; scroll-snap-align: start; pointer-events: none; user-select: none;
  }
  .ht-card-no-photo {
    width: 120px; height: 120px; flex-shrink: 0;
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px;
  }
  .ht-card-no-photo-icon { font-size: 20px; opacity: 0.15; }
  .ht-card-no-photo-text {
    font-size: 8px; font-weight: 700; letter-spacing: 1.5px;
    text-transform: uppercase; color: rgba(255,255,255,0.15);
  }
  .ht-card-photo-count {
    position: absolute; bottom: 5px; right: 5px;
    background: rgba(0,0,0,0.65); color: #FFF;
    font-size: 9px; font-weight: 700; padding: 2px 5px; border-radius: 8px; pointer-events: none;
  }
  .ht-card-info {
    flex: 1; min-width: 0; padding: 11px 13px;
    display: flex; flex-direction: column; justify-content: space-between;
  }
  .ht-card-price {
    font-family: 'Bebas Neue', sans-serif; font-size: 22px;
    color: #1A1A1A; letter-spacing: 0.5px; line-height: 1; margin-bottom: 2px;
  }
  .ht-card-address {
    font-size: 12px; font-weight: 600; color: #444; line-height: 1.35; margin-bottom: 5px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .ht-card-meta { display: flex; gap: 6px; align-items: center; margin-bottom: 7px; }
  .ht-card-meta-item { font-size: 11px; font-weight: 500; color: #999; }
  .ht-card-meta-sep { font-size: 9px; color: #DDD; }
  .ht-status-badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 8px; border-radius: 6px;
    font-size: 9px; font-weight: 800; letter-spacing: 0.8px; text-transform: uppercase;
  }
  .ht-status-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }

  /* ── MODAL ── */
  .ht-modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.5);
    z-index: 200; display: flex; align-items: flex-end;
    animation: htOverlayIn 0.2s ease; backdrop-filter: blur(2px);
  }
  @keyframes htOverlayIn { from { opacity: 0; } to { opacity: 1; } }
  .ht-modal {
    background: #FFF; border-radius: 22px 22px 0 0;
    width: 100%; max-height: 90vh; overflow-y: auto; overflow-x: hidden;
    animation: htModalUp 0.32s cubic-bezier(0.34, 1.2, 0.64, 1);
    -webkit-overflow-scrolling: touch;
  }
  .ht-modal-handle {
    width: 36px; height: 4px; background: #E0E0E0; border-radius: 2px; margin: 10px auto 0;
  }
  .ht-modal-gallery {
    position: relative; width: 100%; height: 220px; background: #1A1A1A; overflow: hidden; margin-top: 10px;
  }
  .ht-modal-gallery-track {
    display: flex; height: 100%; transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  .ht-modal-gallery-slide { flex-shrink: 0; width: 100%; height: 100%; object-fit: cover; user-select: none; }
  .ht-modal-gallery-empty {
    width: 100%; height: 100%;
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px;
  }
  .ht-modal-gallery-empty-icon { font-size: 32px; opacity: 0.15; }
  .ht-modal-gallery-empty-text {
    font-size: 10px; font-weight: 700; letter-spacing: 2px;
    text-transform: uppercase; color: rgba(255,255,255,0.2);
  }
  .ht-modal-gallery-dots {
    position: absolute; bottom: 10px; left: 50%; transform: translateX(-50%);
    display: flex; gap: 5px; z-index: 2;
  }
  .ht-modal-gallery-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: rgba(255,255,255,0.3); border: none; padding: 0;
    cursor: pointer; transition: all 0.2s; flex-shrink: 0;
  }
  .ht-modal-gallery-dot.active { background: #F5F500; width: 18px; border-radius: 3px; }
  .ht-modal-gallery-count {
    position: absolute; top: 10px; right: 10px;
    background: rgba(0,0,0,0.6); color: #FFF;
    font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 10px; z-index: 2;
  }
  .ht-modal-gallery-arrow {
    position: absolute; top: 50%; transform: translateY(-50%);
    background: rgba(0,0,0,0.4); border: none; color: #FFF;
    width: 30px; height: 30px; border-radius: 50%; font-size: 16px;
    cursor: pointer; z-index: 3; display: flex; align-items: center; justify-content: center;
  }
  .ht-modal-gallery-arrow.left { left: 10px; }
  .ht-modal-gallery-arrow.right { right: 10px; }
  .ht-modal-close {
    position: absolute; top: 10px; left: 10px;
    background: rgba(0,0,0,0.55); border: none; color: #FFF;
    width: 30px; height: 30px; border-radius: 50%; font-size: 13px;
    cursor: pointer; z-index: 4; display: flex; align-items: center; justify-content: center;
  }
  .ht-modal-body { padding: 14px 18px 32px; }
  .ht-modal-top-row {
    display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 2px;
  }
  .ht-modal-price {
    font-family: 'Bebas Neue', sans-serif; font-size: 32px; color: #1A1A1A; line-height: 1;
  }
  .ht-modal-address { font-size: 13px; font-weight: 600; color: #666; margin-bottom: 12px; line-height: 1.4; }
  .ht-modal-meta-row {
    display: flex; margin-bottom: 14px; border: 1.5px solid #EBEBEB; border-radius: 10px; overflow: hidden;
  }
  .ht-modal-meta-item { flex: 1; padding: 9px 6px; text-align: center; border-right: 1.5px solid #EBEBEB; }
  .ht-modal-meta-item:last-child { border-right: none; }
  .ht-modal-meta-val {
    font-family: 'Bebas Neue', sans-serif; font-size: 20px; color: #1A1A1A; display: block; line-height: 1;
  }
  .ht-modal-meta-lbl {
    font-size: 9px; font-weight: 700; letter-spacing: 1.5px;
    text-transform: uppercase; color: #BBB; margin-top: 3px; display: block;
  }
  .ht-modal-section-title {
    font-size: 9px; font-weight: 800; letter-spacing: 2.5px; text-transform: uppercase; color: #BBB;
    margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #F0F0F0;
  }
  .ht-modal-notes {
    font-size: 13px; color: #444; line-height: 1.7;
    padding: 10px 13px; background: #FAFAFA; border-radius: 8px;
    border-left: 3px solid #F5F500; margin-bottom: 14px;
  }
  .ht-modal-status-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-bottom: 14px; }
  .ht-modal-status-opt {
    padding: 8px 10px; border-radius: 8px; border: 1.5px solid #EBEBEB;
    background: #FAFAFA; font-family: 'Inter', sans-serif;
    font-size: 10px; font-weight: 800; letter-spacing: 0.8px;
    text-transform: uppercase; cursor: pointer; color: #999; text-align: center; transition: all 0.15s;
  }
  .ht-modal-status-opt.selected { background: #1A1A1A; border-color: #1A1A1A; color: #F5F500; }
  .ht-modal-status-opt:active { transform: scale(0.97); }
  .ht-photo-thumbs { display: flex; gap: 7px; flex-wrap: wrap; margin-bottom: 8px; }
  .ht-photo-thumb {
    position: relative; width: 68px; height: 68px;
    border-radius: 8px; overflow: hidden; flex-shrink: 0; border: 1.5px solid #EBEBEB;
  }
  .ht-photo-thumb img { width: 100%; height: 100%; object-fit: cover; }
  .ht-photo-thumb-del {
    position: absolute; top: 3px; right: 3px; width: 18px; height: 18px; border-radius: 50%;
    background: rgba(0,0,0,0.7); border: none; color: #FFF; font-size: 9px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
  }
  .ht-photo-add-btn {
    display: flex; align-items: center; justify-content: center; gap: 6px;
    padding: 9px 12px; border-radius: 8px; border: 1.5px dashed #CFCFCF;
    background: transparent; font-family: 'Inter', sans-serif;
    font-size: 11px; font-weight: 700; color: #AAA; cursor: pointer; width: 100%; transition: all 0.15s;
  }
  .ht-photo-add-btn:active { background: #F8F8F8; }
  .ht-modal-actions { display: flex; gap: 7px; flex-wrap: wrap; margin-top: 14px; }
  .ht-action-btn {
    padding: 8px 14px; border-radius: 8px; border: 1.5px solid #EBEBEB;
    background: #FFF; font-family: 'Inter', sans-serif;
    font-size: 11px; font-weight: 700; color: #444; cursor: pointer; transition: all 0.15s;
  }
  .ht-action-btn:active { transform: scale(0.97); }
  .ht-action-btn.danger { border-color: #FFD0D0; color: #CC0000; }
  .ht-action-btn.primary { background: #1A1A1A; border-color: #1A1A1A; color: #F5F500; }
  .ht-modal-link-btn {
    display: inline-flex; align-items: center; gap: 6px;
    background: #F5F500; color: #1A1A1A; border: none;
    font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 800;
    padding: 9px 16px; border-radius: 8px; cursor: pointer;
    text-decoration: none; margin-bottom: 14px; transition: all 0.15s;
  }
  .ht-modal-link-btn:active { transform: scale(0.97); }
  .ht-divider { border: none; border-top: 1px solid #F0F0F0; margin: 12px 0; }

  /* ── FORM ── */
  .ht-form-hero {
    background: #1A1A1A; margin: 0 -16px; padding: 28px 24px 24px; margin-bottom: 16px;
  }
  .ht-form-eyebrow {
    font-size: 9px; font-weight: 800; letter-spacing: 3px;
    text-transform: uppercase; color: #F5F500; margin-bottom: 6px;
  }
  .ht-form-title {
    font-family: 'Bebas Neue', sans-serif; font-size: 36px; color: #FFF; letter-spacing: 1px; line-height: 1;
  }
  .ht-form-block {
    background: #FFF; border-radius: 14px; padding: 16px; border: 1.5px solid #EBEBEB; margin-bottom: 10px;
  }
  .ht-form-block-title {
    font-size: 9px; font-weight: 800; letter-spacing: 2.5px; text-transform: uppercase; color: #BBB;
    margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid #F5F5F5;
  }
  .ht-label {
    display: block; font-size: 10px; font-weight: 800; color: #AAA;
    letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 5px;
  }
  .ht-input {
    width: 100%; padding: 10px 13px; border: 1.5px solid #EBEBEB;
    border-radius: 10px; font-family: 'Inter', sans-serif; font-size: 14px;
    color: #1A1A1A; background: #FAFAFA; margin-bottom: 12px;
    outline: none; transition: border-color 0.15s; font-weight: 500;
  }
  .ht-input:focus { border-color: #1A1A1A; background: #FFF; }
  .ht-textarea {
    width: 100%; padding: 10px 13px; border: 1.5px solid #EBEBEB;
    border-radius: 10px; font-family: 'Inter', sans-serif; font-size: 14px;
    color: #1A1A1A; background: #FAFAFA; min-height: 80px; resize: vertical;
    outline: none; line-height: 1.6; transition: border-color 0.15s;
  }
  .ht-textarea:focus { border-color: #1A1A1A; background: #FFF; }
  .ht-row { display: flex; gap: 10px; }
  .ht-half { flex: 1; min-width: 0; }
  .ht-status-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .ht-status-form-opt {
    padding: 10px 12px; border-radius: 10px; border: 1.5px solid #EBEBEB;
    background: #FAFAFA; font-family: 'Inter', sans-serif;
    font-size: 10px; font-weight: 800; letter-spacing: 0.8px;
    text-transform: uppercase; cursor: pointer; transition: all 0.15s; color: #AAA; text-align: center;
  }
  .ht-status-form-opt.selected { background: #1A1A1A; border-color: #1A1A1A; color: #F5F500; }
  .ht-submit-btn {
    width: 100%; padding: 15px; background: #1A1A1A; color: #F5F500;
    border: none; border-radius: 12px; font-family: 'Inter', sans-serif;
    font-size: 13px; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase;
    cursor: pointer; margin-top: 4px; transition: all 0.15s;
  }
  .ht-submit-btn:active { transform: scale(0.99); opacity: 0.9; }
  .ht-cancel-btn {
    width: 100%; padding: 13px; background: transparent; color: #AAA;
    border: 1.5px solid #EBEBEB; border-radius: 12px; font-family: 'Inter', sans-serif;
    font-size: 12px; font-weight: 700; cursor: pointer; margin-top: 8px;
  }

  /* ── ARCHIVE ── */
  .ht-archive-banner {
    background: #1A1A1A; border-radius: 14px; padding: 20px;
    margin-bottom: 20px; display: flex; align-items: center; gap: 16px;
  }
  .ht-archive-num { font-family: 'Bebas Neue', sans-serif; font-size: 52px; color: #F5F500; line-height: 1; flex-shrink: 0; }
  .ht-archive-text { font-size: 12px; color: #666; line-height: 1.7; font-weight: 500; }

  /* ── EMPTY ── */
  .ht-empty { text-align: center; padding: 60px 20px 40px; }
  .ht-empty-num { font-family: 'Bebas Neue', sans-serif; font-size: 100px; color: #EBEBEB; line-height: 1; margin-bottom: 8px; }
  .ht-empty-title { font-size: 16px; font-weight: 800; color: #CCC; margin-bottom: 6px; }
  .ht-empty-sub { font-size: 13px; color: #CCC; line-height: 1.6; }

  /* ── LOADING ── */
  .ht-loading {
    min-height: 100vh; background: #1A1A1A;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 16px;
  }
  .ht-loading-logo {
    font-family: 'Bebas Neue', sans-serif; font-size: 48px; color: #FFF; letter-spacing: 2px;
  }
  .ht-loading-logo span { color: #F5F500; }
  .ht-spinner {
    width: 28px; height: 28px; border: 3px solid #333;
    border-top-color: #F5F500; border-radius: 50%;
    animation: htSpin 0.7s linear infinite;
  }
  @keyframes htSpin { to { transform: rotate(360deg); } }

  body.ht-modal-open { overflow: hidden; }

  /* ── FOOTER ── */
  .ht-footer {
    background: #1A1A1A; margin: 40px -16px 0;
    padding: 36px 24px 32px;
    position: relative; overflow: hidden;
  }
  .ht-footer::before {
    content: ""; position: absolute; bottom: -40px; right: -40px;
    width: 160px; height: 160px; background: #F5F500;
    border-radius: 50%; opacity: 0.05;
  }
  .ht-footer-logo {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 28px; letter-spacing: 1px; color: #FFF; line-height: 1; margin-bottom: 4px;
  }
  .ht-footer-logo span {
    display: inline-block; background: #F5F500; color: #1A1A1A;
    padding: 0 4px; margin-left: 1px; border-radius: 3px; font-size: 26px;
  }
  .ht-footer-tagline { font-size: 12px; color: #555; margin-bottom: 20px; font-style: italic; line-height: 1.5; }
  .ht-footer-divider { border: none; border-top: 1px solid #2A2A2A; margin: 0 0 20px; }
  .ht-footer-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
  .ht-footer-dev-label { font-size: 9px; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; color: #444; margin-bottom: 5px; }
  .ht-footer-dev-name { font-size: 14px; font-weight: 700; color: #FFF; margin-bottom: 4px; }
  .ht-footer-dev-email { font-size: 12px; color: #555; text-decoration: none; transition: color 0.15s; }
  .ht-footer-dev-email:hover { color: #F5F500; }
  .ht-footer-copy { font-size: 11px; color: #333; text-align: right; line-height: 1.6; align-self: flex-end; }
  .ht-footer-year { color: #F5F500; font-weight: 700; }


  /* ── SEARCH & SORT TOOLBAR ── */
  .ht-toolbar { display: flex; gap: 8px; margin-top: 16px; margin-bottom: 4px; align-items: center; }
  .ht-search-wrap { flex: 1; position: relative; }
  .ht-search-icon { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); font-size: 13px; pointer-events: none; opacity: 0.35; }
  .ht-search { width: 100%; padding: 9px 12px 9px 33px; border: 1.5px solid #EBEBEB; border-radius: 10px; font-family: 'Inter', sans-serif; font-size: 13px; color: #1A1A1A; background: #FFF; outline: none; transition: border-color 0.15s; font-weight: 500; }
  .ht-search:focus { border-color: #1A1A1A; }
  .ht-search::placeholder { color: #BBB; }
  .ht-sort-btn { padding: 9px 13px; border: 1.5px solid #EBEBEB; border-radius: 10px; background: #FFF; font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 700; color: #888; cursor: pointer; transition: all 0.15s; white-space: nowrap; display: flex; align-items: center; gap: 5px; flex-shrink: 0; }
  .ht-sort-btn.active { background: #1A1A1A; border-color: #1A1A1A; color: #F5F500; }
  .ht-sort-wrap { position: relative; }
  .ht-sort-menu { position: absolute; right: 0; top: calc(100% + 6px); z-index: 50; background: #FFF; border: 1.5px solid #EBEBEB; border-radius: 12px; padding: 6px; min-width: 180px; box-shadow: 0 8px 24px rgba(0,0,0,0.1); animation: htFadeUp 0.15s ease both; }
  .ht-sort-option { width: 100%; padding: 9px 12px; background: transparent; border: none; font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 600; color: #555; cursor: pointer; text-align: left; border-radius: 8px; transition: background 0.12s; display: flex; align-items: center; justify-content: space-between; }
  .ht-sort-option:hover { background: #F8F8F8; }
  .ht-sort-option.active { color: #1A1A1A; font-weight: 800; }
  .ht-sort-option.active::after { content: "✓"; color: #F5F500; }

  /* ── STAR RATING ── */
  .ht-stars { display: flex; gap: 1px; align-items: center; }
  .ht-star { font-size: 15px; cursor: pointer; transition: transform 0.1s; line-height: 1; background: none; border: none; padding: 2px; color: #F5F500; }
  .ht-star:active { transform: scale(1.3); }
  .ht-star-sm { font-size: 11px; }

  /* ── PRICE PER SQFT ── */
  .ht-ppsf { font-size: 10px; font-weight: 700; color: #AAA; letter-spacing: 0.3px; margin-bottom: 2px; }


  /* ── COMPARE LIGHTBOX ── */
  .ht-clb-overlay {
    position: fixed; inset: 0; background: #000;
    z-index: 9999; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 20px;
  }
  .ht-clb-close {
    position: fixed; top: 16px; right: 16px;
    background: rgba(255,255,255,0.2); border: none; color: #FFF;
    width: 40px; height: 40px; border-radius: 50%; font-size: 18px;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    z-index: 10000;
  }
  .ht-clb-img {
    max-width: 90vw; max-height: 70vh; width: auto; height: auto;
    object-fit: contain; border-radius: 8px; user-select: none;
    display: block;
  }
  .ht-clb-nav {
    display: flex; align-items: center; gap: 20px; margin-top: 16px;
  }
  .ht-clb-nav-btn {
    background: rgba(255,255,255,0.15); border: none; color: #FFF;
    width: 40px; height: 40px; border-radius: 50%; font-size: 18px;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    transition: background 0.15s;
  }
  .ht-clb-nav-btn:active { background: rgba(255,255,255,0.3); }
  .ht-clb-counter {
    font-family: 'Inter', sans-serif; font-size: 13px;
    color: rgba(255,255,255,0.5); min-width: 50px; text-align: center;
  }
  .ht-clb-dots {
    display: flex; gap: 6px; margin-top: 12px; align-items: center;
  }
  .ht-clb-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: rgba(255,255,255,0.3); border: none; padding: 0;
    cursor: pointer; transition: all 0.2s;
  }
  .ht-clb-dot.active { background: #F5F500; width: 18px; border-radius: 3px; }
  .ht-clb-address {
    font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 600;
    color: rgba(255,255,255,0.5); margin-top: 8px; text-align: center;
  }


  /* ── MAP PREVIEW ── */
  .ht-map-wrap {
    border-radius: 12px; overflow: hidden;
    border: 1.5px solid #EBEBEB; margin-bottom: 14px;
    position: relative; background: #F0F0F0;
  }
  .ht-map-iframe {
    width: 100%; height: 180px; border: none; display: block;
  }
  .ht-map-footer {
    display: flex; align-items: center; justify-content: space-between;
    padding: 9px 12px; background: #FAFAFA; border-top: 1px solid #F0F0F0;
  }
  .ht-map-address {
    font-size: 11px; font-weight: 600; color: #888;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1;
  }
  .ht-map-btn {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 5px 12px; background: #1A1A1A; color: #F5F500;
    border: none; border-radius: 6px; font-family: 'Inter', sans-serif;
    font-size: 11px; font-weight: 800; cursor: pointer;
    letter-spacing: 0.3px; transition: opacity 0.15s;
    text-decoration: none; flex-shrink: 0; margin-left: 10px;
  }
  .ht-map-btn:active { opacity: 0.85; }

  /* ── COMPARE BAR ── */
  .ht-compare-bar { position: fixed; bottom: 0; left: 0; right: 0; z-index: 150; background: #1A1A1A; padding: 12px 16px 24px; display: flex; align-items: center; justify-content: space-between; gap: 12px; border-top: 2px solid #F5F500; animation: htSlideUp 0.25s cubic-bezier(0.34,1.2,0.64,1); }
  @keyframes htSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
  .ht-compare-bar-left { display: flex; align-items: center; gap: 10px; flex: 1; min-width: 0; overflow: hidden; }
  .ht-compare-bar-label { font-size: 11px; font-weight: 800; color: #F5F500; letter-spacing: 1px; text-transform: uppercase; white-space: nowrap; }
  .ht-compare-bar-homes { display: flex; gap: 6px; overflow: hidden; }
  .ht-compare-bar-chip { background: #2A2A2A; border-radius: 6px; padding: 4px 8px; font-size: 11px; font-weight: 600; color: #FFF; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 110px; }
  .ht-compare-btn { background: #F5F500; color: #1A1A1A; border: none; font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 800; padding: 10px 18px; border-radius: 8px; cursor: pointer; white-space: nowrap; flex-shrink: 0; transition: all 0.15s; }
  .ht-compare-btn:active { transform: scale(0.97); }
  .ht-compare-clear { background: transparent; border: none; color: #555; font-size: 16px; cursor: pointer; padding: 4px; flex-shrink: 0; }
  .ht-card-compare-check { position: absolute; top: 6px; left: 6px; z-index: 2; width: 22px; height: 22px; border-radius: 6px; border: 2px solid rgba(255,255,255,0.5); background: rgba(0,0,0,0.35); cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; transition: all 0.15s; }
  .ht-card-compare-check.checked { background: #F5F500; border-color: #F5F500; color: #1A1A1A; }

  /* ── COMPARE VIEW ── */
  .ht-compare-overlay { position: fixed; inset: 0; background: #F8F8F8; z-index: 300; overflow-y: auto; animation: htFadeUp 0.2s ease; }
  .ht-compare-header { background: #1A1A1A; padding: 14px 20px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 10; }
  .ht-compare-title { font-family: 'Bebas Neue', sans-serif; font-size: 22px; color: #FFF; letter-spacing: 1px; }
  .ht-compare-title span { color: #F5F500; }
  .ht-compare-close { background: rgba(255,255,255,0.1); border: none; color: #FFF; width: 30px; height: 30px; border-radius: 50%; font-size: 13px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
  .ht-compare-table { padding: 12px; max-width: 900px; margin: 0 auto; }

  /* Compact photo strip — fixed height, not full bleed */
  .ht-compare-photos-row { display: grid; gap: 8px; margin-bottom: 10px; }
  .ht-compare-photo { height: 180px; border-radius: 10px; overflow: hidden; background: #1A1A1A; border: 1.5px solid #EBEBEB; flex-shrink: 0; cursor: pointer; transition: opacity 0.15s; }
  .ht-compare-photo:active { opacity: 0.85; }
  .ht-compare-photo { position: relative; }
  .ht-compare-photo img { width: 100%; height: 100%; object-fit: cover; }
  .ht-compare-photo-empty { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 20px; opacity: 0.2; }

  /* Compact rows — label on left, values on right as columns */
  .ht-compare-row {
    display: flex; align-items: stretch;
    margin-bottom: 6px; background: #FFF;
    border-radius: 10px; border: 1.5px solid #EBEBEB; overflow: hidden;
  }
  .ht-compare-row-label {
    font-size: 9px; font-weight: 800; letter-spacing: 1.5px;
    text-transform: uppercase; color: #AAA;
    padding: 10px 12px; width: 90px; min-width: 90px;
    border-right: 1.5px solid #F0F0F0;
    display: flex; align-items: center; background: #FAFAFA;
    line-height: 1.3;
  }
  .ht-compare-cells { display: flex; flex: 1; }
  .ht-compare-cell { flex: 1; padding: 8px 12px; border-right: 1px solid #F5F5F5; display: flex; flex-direction: column; justify-content: center; }
  .ht-compare-cell:last-child { border-right: none; }
  .ht-compare-cell-val { font-family: 'Bebas Neue', sans-serif; font-size: 18px; color: #1A1A1A; display: block; line-height: 1; }
  .ht-compare-cell-val.best { color: #1A1A1A; }
  .ht-compare-cell-address { font-size: 12px; font-weight: 700; color: #1A1A1A; line-height: 1.35; display: block; }
  .ht-compare-winner { display: inline-block; background: #F5F500; color: #1A1A1A; font-size: 7px; font-weight: 900; letter-spacing: 1px; text-transform: uppercase; padding: 2px 5px; border-radius: 3px; margin-top: 3px; }

  /* ══════════════════════════════════════════
     DESKTOP RESPONSIVE — 768px and up
  ══════════════════════════════════════════ */
  @media (min-width: 768px) {

    /* ── HEADER ── */
    .ht-header { padding: 0 40px; height: 64px; }
    .ht-logo { font-size: 32px; }

    /* ── NAV becomes horizontal full-width bar with more spacing ── */
    .ht-nav { padding: 0 40px; gap: 4px; }
    .ht-nav-btn { flex: none; padding: 16px 24px; font-size: 12px; letter-spacing: 1.5px; }

    /* ── CONTENT wider, centered ── */
    .ht-content { padding: 0 40px 80px; max-width: 1200px; }

    /* ── HERO more spacious ── */
    .ht-hero { margin: 0 -40px; padding: 56px 48px 48px; display: flex; align-items: center; gap: 48px; }
    .ht-hero::before { width: 340px; height: 340px; top: -80px; right: -60px; }
    .ht-hero-content { flex: 1; }
    .ht-hero-headline { font-size: 80px; }
    .ht-hero-sub { font-size: 15px; max-width: 360px; }
    .ht-hero-cta { max-width: 280px; padding: 16px 22px; font-size: 14px; }
    .ht-hero-stats { gap: 36px; }
    .ht-hero-stat-num { font-size: 48px; }
    .ht-hero-stat-lbl { font-size: 11px; }

    /* ── LISTINGS GRID — 2 columns on desktop ── */
    .ht-listings-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }
    .ht-listings-grid .ht-card { margin-bottom: 0; }

    /* ── CARDS taller on desktop ── */
    .ht-card { height: 140px; }
    .ht-card-photos-wrap { width: 140px; }
    .ht-card-photos { width: 140px; height: 140px; }
    .ht-card-photo { width: 140px; height: 140px; }
    .ht-card-no-photo { width: 140px; height: 140px; }
    .ht-card-price { font-size: 24px; }
    .ht-card-address { font-size: 13px; }
    .ht-card-info { padding: 14px 16px; }

    /* ── MODAL becomes centered dialog on desktop ── */
    .ht-modal-overlay { align-items: center; justify-content: center; padding: 24px; }
    .ht-modal {
      border-radius: 20px;
      max-width: 580px;
      max-height: 88vh;
      width: 100%;
    }
    .ht-modal-handle { display: none; }
    .ht-modal-gallery { height: 300px; margin-top: 0; border-radius: 20px 20px 0 0; }

    /* ── FORM wider, two-col fields ── */
    .ht-form-hero { margin: 0 -40px; padding: 40px 48px 36px; }
    .ht-form-title { font-size: 48px; }
    .ht-form-layout {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      align-items: start;
    }
    .ht-form-layout .ht-form-block { margin-bottom: 0; }
    .ht-form-layout .ht-form-block-full { grid-column: span 2; }

    /* ── AUTH screen centered card ── */
    .ht-auth { flex-direction: row; }
    .ht-auth-hero { padding: 60px 56px; max-width: 520px; }
    .ht-auth-headline { font-size: 88px; }
    .ht-auth-card {
      flex: 1; border-radius: 0; padding: 60px 56px;
      display: flex; flex-direction: column; justify-content: center;
      max-width: 480px; margin-left: auto;
    }

    /* ── SECTION ROW ── */
    .ht-section-row { padding: 28px 0 16px; }
    .ht-section-title { font-size: 12px; }

    /* ── FILTERS ── */
    .ht-filters { margin-bottom: 20px; gap: 8px; }
    .ht-filter { padding: 7px 18px; font-size: 12px; }

    /* ── EMPTY STATE ── */
    .ht-empty { padding: 80px 40px 60px; }
    .ht-empty-num { font-size: 140px; }

    /* ── ARCHIVE BANNER ── */
    .ht-archive-banner { padding: 28px; }
    .ht-archive-num { font-size: 64px; }

    /* ── USER MENU ── */
    .ht-user-menu { right: 40px; }
  }

  /* ── EXTRA WIDE — 3 columns at 1100px ── */
  @media (min-width: 1100px) {
    .ht-listings-grid { grid-template-columns: 1fr 1fr 1fr; }
  }
`;

function useGlobalStyles() {
  useEffect(() => {
    const id = "ht-styles";
    if (!document.getElementById(id)) {
      const el = document.createElement("style");
      el.id = id; el.textContent = GLOBAL_CSS;
      document.head.appendChild(el);
    }
    return () => { document.getElementById(id)?.remove(); };
  }, []);
}

// ─────────────────────────────────────────────
// PHOTO UPLOADER
// ─────────────────────────────────────────────
function PhotoUploader({ photos, onAdd, onDelete }) {
  const inputRef = useRef(null);
  async function handleFiles(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const compressed = await Promise.all(files.map(f => compressImage(f)));
    onAdd(compressed);
    e.target.value = "";
  }
  return (
    <div>
      {photos.length > 0 && (
        <div className="ht-photo-thumbs">
          {photos.map((src, i) => (
            <div key={i} className="ht-photo-thumb">
              <img src={src} alt="" />
              <button className="ht-photo-thumb-del" onClick={() => onDelete(i)}>✕</button>
            </div>
          ))}
        </div>
      )}
      <button className="ht-photo-add-btn" onClick={() => inputRef.current?.click()}>
        + Add Photos
      </button>
      <input ref={inputRef} type="file" accept="image/*" multiple
        style={{ display: "none" }} onChange={handleFiles} />
    </div>
  );
}

// ─────────────────────────────────────────────
// MODAL GALLERY
// ─────────────────────────────────────────────
function ModalGallery({ photos }) {
  const [idx, setIdx] = useState(0);
  const touchStartX = useRef(null);
  useEffect(() => setIdx(0), [photos?.length]);
  function goTo(i) { setIdx(Math.max(0, Math.min(i, photos.length - 1))); }
  function onTouchStart(e) { touchStartX.current = e.touches[0].clientX; }
  function onTouchEnd(e) {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0 && idx < photos.length - 1) goTo(idx + 1);
      if (dx > 0 && idx > 0) goTo(idx - 1);
    }
    touchStartX.current = null;
  }
  if (!photos || photos.length === 0) {
    return (
      <div className="ht-modal-gallery">
        <div className="ht-modal-gallery-empty">
          <div className="ht-modal-gallery-empty-icon">📷</div>
          <div className="ht-modal-gallery-empty-text">No photos yet</div>
        </div>
      </div>
    );
  }
  return (
    <div className="ht-modal-gallery" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="ht-modal-gallery-track" style={{ transform: `translateX(-${idx * 100}%)` }}>
        {photos.map((src, i) => (
          <img key={i} className="ht-modal-gallery-slide" src={src} alt="" draggable={false} />
        ))}
      </div>
      {photos.length > 1 && <>
        <span className="ht-modal-gallery-count">{idx + 1} / {photos.length}</span>
        <div className="ht-modal-gallery-dots">
          {photos.map((_, i) => (
            <button key={i} className={`ht-modal-gallery-dot${i === idx ? " active" : ""}`} onClick={() => goTo(i)} />
          ))}
        </div>
        {idx > 0 && <button className="ht-modal-gallery-arrow left" onClick={() => goTo(idx - 1)}>‹</button>}
        {idx < photos.length - 1 && <button className="ht-modal-gallery-arrow right" onClick={() => goTo(idx + 1)}>›</button>}
      </>}
    </div>
  );
}

// ─────────────────────────────────────────────
// LISTING MODAL
// ─────────────────────────────────────────────
function ListingModal({ home, onClose, onEdit, onArchive, onDelete, onStatusChange, onPhotosChange }) {
  const photos = home.photos || [];
  const price = formatPrice(home.price);
  useEffect(() => {
    document.body.classList.add("ht-modal-open");
    return () => document.body.classList.remove("ht-modal-open");
  }, []);
  return (
    <div className="ht-modal-overlay" onClick={onClose}>
      <div className="ht-modal" onClick={e => e.stopPropagation()}>
        <div className="ht-modal-handle" />
        <div style={{ position: "relative" }}>
          <ModalGallery photos={photos} />
          <button className="ht-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="ht-modal-body">
          <div className="ht-modal-top-row">
            <div className="ht-modal-price">{price || "No price"}</div>
            <span className="ht-status-badge"
              style={{ background: STATUS_MAP[home.status]?.bg, color: STATUS_MAP[home.status]?.color }}>
              <span className="ht-status-dot" style={{ background: STATUS_MAP[home.status]?.dot }} />
              {STATUS_MAP[home.status]?.label}
            </span>
          </div>
          <div className="ht-modal-address">{home.address}</div>
          <div style={{ marginBottom: 12 }}>
            <StarRating rating={home.rating || 0} onRate={(r) => onStatusChange(home.id + "__rating", r)} />
          </div>


          {(home.beds || home.baths || home.sqft) && (
            <div className="ht-modal-meta-row">
              {home.beds && <div className="ht-modal-meta-item">
                <span className="ht-modal-meta-val">{home.beds}</span>
                <span className="ht-modal-meta-lbl">Beds</span>
              </div>}
              {home.baths && <div className="ht-modal-meta-item">
                <span className="ht-modal-meta-val">{home.baths}</span>
                <span className="ht-modal-meta-lbl">Baths</span>
              </div>}
              {home.sqft && <div className="ht-modal-meta-item">
                <span className="ht-modal-meta-val">{parseInt(home.sqft).toLocaleString()}</span>
                <span className="ht-modal-meta-lbl">Sq Ft</span>
              </div>}
            </div>
          )}
          {home.notes && <>
            <div className="ht-modal-section-title">Notes</div>
            <div className="ht-modal-notes">{home.notes}</div>
          </>}
          {home.link && (
            <a href={home.link} target="_blank" rel="noreferrer" className="ht-modal-link-btn">
              View Original Listing ↗
            </a>
          )}
          <hr className="ht-divider" />
          <div className="ht-modal-section-title">Update Status</div>
          <div className="ht-modal-status-grid">
            {STATUSES.map(s => (
              <button key={s.value}
                className={`ht-modal-status-opt${home.status === s.value ? " selected" : ""}`}
                onClick={() => onStatusChange(home.id, s.value)}>
                {s.label}
              </button>
            ))}
          </div>
          <hr className="ht-divider" />
          <div className="ht-modal-section-title">Photos ({photos.length})</div>
          <PhotoUploader
            photos={photos}
            onAdd={(newPhotos) => onPhotosChange(home.id, [...photos, ...newPhotos])}
            onDelete={(i) => onPhotosChange(home.id, photos.filter((_, j) => j !== i))}
          />
          <hr className="ht-divider" />

          {/* Map at the bottom */}
          {home.address && (
            <>
              <div className="ht-modal-section-title">Location</div>
              <div className="ht-map-wrap">
                <iframe
                  className="ht-map-iframe"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(home.address)}&output=embed&z=15`}
                  title="Map"
                />
                <div className="ht-map-footer">
                  <span className="ht-map-address">{home.address}</span>
                  <a
                    className="ht-map-btn"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(home.address)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open Maps ↗
                  </a>
                </div>
              </div>
              <hr className="ht-divider" />
            </>
          )}

          <div className="ht-modal-actions">
            <button className="ht-action-btn" onClick={() => { onClose(); onEdit(home); }}>Edit Details</button>
            <button className="ht-action-btn" onClick={() => { onArchive(home.id); onClose(); }}>
              {home.archived ? "Restore" : "Archive"}
            </button>
            <button className="ht-action-btn danger" onClick={() => { onDelete(home.id); onClose(); }}>Delete</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// LISTING CARD
// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
// STAR RATING
// ─────────────────────────────────────────────
function StarRating({ rating = 0, onRate, size = "normal", readonly = false }) {
  const [hover, setHover] = useState(0);
  const cls = "ht-star" + (size === "small" ? " ht-star-sm" : "");
  return (
    <div className="ht-stars">
      {[1,2,3,4,5].map(n => (
        <button key={n} className={cls}
          onClick={readonly ? undefined : (e) => { e.stopPropagation(); onRate(n === rating ? 0 : n); }}
          onMouseEnter={readonly ? undefined : () => setHover(n)}
          onMouseLeave={readonly ? undefined : () => setHover(0)}
          style={{ cursor: readonly ? "default" : "pointer" }}>
          {(hover || rating) >= n ? "★" : "☆"}
        </button>
      ))}
    </div>
  );
}

function ListingCard({ home, onClick, index, compareMode, isCompared, onToggleCompare, onRate }) {
  const photos = home.photos || [];
  const price = formatPrice(home.price);
  const status = STATUS_MAP[home.status];
  const priceNum = parseFloat(String(home.price || "").replace(/[^0-9.]/g, ""));
  const sqftNum = parseFloat(home.sqft || "");
  const ppsf = priceNum > 0 && sqftNum > 0 ? "$" + Math.round(priceNum / sqftNum) + "/ft²" : null;
  return (
    <div className={`ht-card${home.archived ? " archived" : ""}`}
      style={{ animationDelay: `${index * 0.05}s`, outline: isCompared ? "2.5px solid #F5F500" : "none", outlineOffset: "-2px" }}
      onClick={() => !compareMode ? onClick() : onToggleCompare(home.id)}>
      <div className="ht-card-photos-wrap">
        <div className="ht-card-photos" onClick={compareMode ? undefined : e => e.stopPropagation()}>
          {photos.length === 0 ? (
            <div className="ht-card-no-photo">
              <div className="ht-card-no-photo-icon">📷</div>
              <div className="ht-card-no-photo-text">No photo</div>
            </div>
          ) : photos.map((src, i) => (
            <img key={i} className="ht-card-photo" src={src} alt="" draggable={false} />
          ))}
        </div>
        {photos.length > 1 && <span className="ht-card-photo-count">{photos.length}</span>}
        {compareMode && (
          <div className={"ht-card-compare-check" + (isCompared ? " checked" : "")}
            onClick={e => { e.stopPropagation(); onToggleCompare(home.id); }}>
            {isCompared ? "✓" : ""}
          </div>
        )}
      </div>
      <div className="ht-card-info">
        <div>
          <div className="ht-card-price">{price || "—"}</div>
          {ppsf && <div className="ht-ppsf">{ppsf}</div>}
          <div className="ht-card-address">{home.address}</div>
          <div className="ht-card-meta">
            {home.beds && <span className="ht-card-meta-item">{home.beds} bd</span>}
            {home.beds && home.baths && <span className="ht-card-meta-sep">·</span>}
            {home.baths && <span className="ht-card-meta-item">{home.baths} ba</span>}
            {home.sqft && (home.beds || home.baths) && <span className="ht-card-meta-sep">·</span>}
            {home.sqft && <span className="ht-card-meta-item">{parseInt(home.sqft).toLocaleString()} ft²</span>}
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:4 }}>
          {status && (
            <span className="ht-status-badge" style={{ background: status.bg, color: status.color }}>
              <span className="ht-status-dot" style={{ background: status.dot }} />
              {status.label}
            </span>
          )}
          <div onClick={e => e.stopPropagation()}>
            <StarRating rating={home.rating || 0} onRate={(r) => onRate(home.id, r)} size="small" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// HOME FORM
// ─────────────────────────────────────────────
function HomeForm({ form, onChange, onPhotosChange, onSubmit, onCancel, isEdit }) {
  const photos = form.photos || [];
  return (
    <div>
      <div className="ht-form-hero">
        <div className="ht-form-eyebrow">{isEdit ? "Editing listing" : "New listing"}</div>
        <div className="ht-form-title">{isEdit ? "Update Home" : "Add a Home"}</div>
      </div>
      <div className="ht-form-block">
        <div className="ht-form-block-title">Photos</div>
        <PhotoUploader
          photos={photos}
          onAdd={(newPhotos) => onPhotosChange([...photos, ...newPhotos])}
          onDelete={(i) => onPhotosChange(photos.filter((_, j) => j !== i))}
        />
      </div>
      <div className="ht-form-block">
        <div className="ht-form-block-title">Location & Price</div>
        <label className="ht-label">Address *</label>
        <input className="ht-input" name="address" value={form.address} onChange={onChange}
          placeholder="123 Oak Street, Austin TX 78701" />
        <div className="ht-row">
          <div className="ht-half">
            <label className="ht-label">Price</label>
            <input className="ht-input" name="price" value={form.price} onChange={onChange} placeholder="450000" />
          </div>
          <div className="ht-half">
            <label className="ht-label">Sq Ft</label>
            <input className="ht-input" name="sqft" value={form.sqft} onChange={onChange} placeholder="1850" />
          </div>
        </div>
        <div className="ht-row">
          <div className="ht-half">
            <label className="ht-label">Beds</label>
            <input className="ht-input" name="beds" value={form.beds} onChange={onChange} placeholder="3" />
          </div>
          <div className="ht-half">
            <label className="ht-label">Baths</label>
            <input className="ht-input" name="baths" value={form.baths} onChange={onChange} placeholder="2" />
          </div>
        </div>
      </div>
      <div className="ht-form-block">
        <div className="ht-form-block-title">Status</div>
        <div className="ht-status-form-grid">
          {STATUSES.map(s => (
            <button key={s.value}
              className={`ht-status-form-opt${form.status === s.value ? " selected" : ""}`}
              onClick={() => onChange({ target: { name: "status", value: s.value } })}>
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <div className="ht-form-block">
        <div className="ht-form-block-title">Details</div>
        <label className="ht-label">Listing URL</label>
        <input className="ht-input" name="link" value={form.link} onChange={onChange}
          placeholder="https://zillow.com/homes/..." type="url" />
        <label className="ht-label">Notes</label>
        <textarea className="ht-textarea" name="notes" value={form.notes} onChange={onChange}
          placeholder="Great backyard, quiet street, needs new kitchen..." />
        <label className="ht-label" style={{ marginTop: 8 }}>Your Rating</label>
        <div style={{ marginBottom: 4 }}>
          <StarRating rating={form.rating || 0} onRate={(r) => onChange({ target: { name: "rating", value: r } })} />
        </div>
      </div>
      <button className="ht-submit-btn" onClick={onSubmit}>
        {isEdit ? "Save Changes" : "Save Home"}
      </button>
      {isEdit && <button className="ht-cancel-btn" onClick={onCancel}>Cancel</button>}
    </div>
  );
}

// ─────────────────────────────────────────────
// COMPARE LIGHTBOX
// ─────────────────────────────────────────────
function CompareLightbox({ home, onClose }) {
  // Only shows photos for the single home that was clicked
  const photos = home.photos || [];
  const [idx, setIdx] = useState(0);
  const touchStartX = useRef(null);

  useEffect(() => {
    document.body.classList.add("ht-modal-open");
    return () => document.body.classList.remove("ht-modal-open");
  }, []);

  function goTo(i) { setIdx(Math.max(0, Math.min(i, photos.length - 1))); }

  function onTouchStart(e) { touchStartX.current = e.touches[0].clientX; }
  function onTouchEnd(e) {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0 && idx < photos.length - 1) goTo(idx + 1);
      if (dx > 0 && idx > 0) goTo(idx - 1);
    }
    touchStartX.current = null;
  }

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goTo(idx - 1);
      if (e.key === "ArrowRight") goTo(idx + 1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  return (
    <div className="ht-clb-overlay" onClick={onClose}>
      <button className="ht-clb-close" onClick={onClose}>✕</button>
      <div onClick={e => e.stopPropagation()}
        onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        {photos.length > 0
          ? <img className="ht-clb-img" src={photos[idx]} alt="" draggable={false} />
          : <div style={{ width:300, height:200, display:"flex", alignItems:"center", justifyContent:"center", color:"rgba(255,255,255,0.2)", fontSize:40 }}>📷</div>
        }
        <div className="ht-clb-address">{home.address.split(",")[0]}</div>
        {photos.length > 1 && (
          <div className="ht-clb-nav">
            <button className="ht-clb-nav-btn" onClick={() => goTo(idx - 1)}
              style={{ opacity: idx === 0 ? 0.3 : 1 }}>‹</button>
            <span className="ht-clb-counter">{idx + 1} / {photos.length}</span>
            <button className="ht-clb-nav-btn" onClick={() => goTo(idx + 1)}
              style={{ opacity: idx === photos.length - 1 ? 0.3 : 1 }}>›</button>
          </div>
        )}
        {photos.length > 1 && photos.length <= 12 && (
          <div className="ht-clb-dots">
            {photos.map((_, i) => (
              <button key={i} className={"ht-clb-dot" + (i === idx ? " active" : "")}
                onClick={() => goTo(i)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// COMPARE VIEW
// ─────────────────────────────────────────────
function CompareView({ homes, onClose }) {
  const [lightboxHome, setLightboxHome] = useState(null);
  const cols = homes.length;
  const gridStyle = { gridTemplateColumns: `repeat(${cols}, 1fr)` };
  const numVal = (v) => parseFloat(String(v || "").replace(/[^0-9.]/g, "")) || 0;

  const prices = homes.map(h => numVal(h.price));
  const sqfts = homes.map(h => numVal(h.sqft));
  const ppsfs = homes.map(h => h.price && h.sqft ? numVal(h.price) / numVal(h.sqft) : 0);
  const ratings = homes.map(h => h.rating || 0);

  const validPrices = prices.filter(n => n > 0);
  const validSqfts = sqfts.filter(n => n > 0);
  const validPpsfs = ppsfs.filter(n => n > 0);
  const validRatings = ratings.filter(n => n > 0);

  const bestPrice = validPrices.length > 1 ? Math.min(...validPrices) : null;
  const bestSqft = validSqfts.length > 1 ? Math.max(...validSqfts) : null;
  const bestPpsf = validPpsfs.length > 1 ? Math.min(...validPpsfs) : null;
  const bestRating = validRatings.length > 1 ? Math.max(...validRatings) : null;

  return (
    <div className="ht-compare-overlay">
      {lightboxHome && (
        <CompareLightbox
          home={lightboxHome}
          onClose={() => setLightboxHome(null)}
        />
      )}
      <div className="ht-compare-header">
        <div className="ht-compare-title">Compare <span>Homes</span></div>
        <button className="ht-compare-close" onClick={onClose}>✕</button>
      </div>
      <div className="ht-compare-table">

        {/* Photos */}
        <div className="ht-compare-photos-row" style={gridStyle}>
          {homes.map(h => (
            <div key={h.id} className="ht-compare-photo"
              onClick={() => setLightboxHome(h)}>
              {(h.photos||[]).length > 0
                ? <img src={h.photos[0]} alt="" />
                : <div className="ht-compare-photo-empty">📷</div>}
              {(h.photos||[]).length > 1 && (
                <div style={{ position:"absolute", bottom:6, right:6, background:"rgba(0,0,0,0.6)", color:"#FFF", fontSize:9, fontWeight:700, padding:"2px 6px", borderRadius:8 }}>
                  {h.photos.length} photos
                </div>
              )}
            </div>
          ))}
        </div>
        {/* Address chips under photos */}
        <div style={{ display:"grid", gap:8, marginBottom:10, gridTemplateColumns:`repeat(${cols}, 1fr)` }}>
          {homes.map(h => (
            <div key={h.id} style={{ fontSize:11, fontWeight:700, color:"#1A1A1A", textAlign:"center", padding:"0 4px", lineHeight:1.3 }}>
              {h.address.split(",")[0]}
            </div>
          ))}
        </div>

        {/* Mini maps row in compare */}
        <div className="ht-compare-row">
          <div className="ht-compare-row-label">Location</div>
          <div className="ht-compare-cells">
            {homes.map(h => (
              <div key={h.id} className="ht-compare-cell" style={{ padding: 8 }}>
                <div style={{ borderRadius:8, overflow:"hidden", border:"1px solid #EBEBEB", background:"#F0F0F0" }}>
                  <iframe
                    style={{ width:"100%", height:100, border:"none", display:"block", pointerEvents:"none" }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(h.address)}&output=embed&z=14`}
                    title="Map"
                  />
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(h.address)}`}
                    target="_blank" rel="noreferrer"
                    style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:4, padding:"6px 8px", background:"#1A1A1A", color:"#F5F500", fontSize:10, fontWeight:800, textDecoration:"none", letterSpacing:"0.3px" }}>
                    Open Maps ↗
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Address & status */}
        <div className="ht-compare-row">
          <div className="ht-compare-row-label">Property</div>
          <div className="ht-compare-cells">
            {homes.map(h => (
              <div key={h.id} className="ht-compare-cell">
                <span className="ht-compare-cell-address">{h.address.split(",")[0]}</span>
                <span className="ht-status-badge" style={{ background: STATUS_MAP[h.status]?.bg, color: STATUS_MAP[h.status]?.color, display:"inline-flex", marginTop:4, fontSize:8 }}>
                  <span className="ht-status-dot" style={{ background: STATUS_MAP[h.status]?.dot }} />
                  {STATUS_MAP[h.status]?.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Price */}
        <div className="ht-compare-row">
          <div className="ht-compare-row-label">Price</div>
          <div className="ht-compare-cells">
            {homes.map((h,i) => (
              <div key={h.id} className="ht-compare-cell">
                <span className={"ht-compare-cell-val" + (prices[i] === bestPrice ? " best" : "")}>
                  {formatPrice(h.price) || "—"}
                </span>
                {prices[i] === bestPrice && <div className="ht-compare-winner">Best Price</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Beds / Baths */}
        <div className="ht-compare-row">
          <div className="ht-compare-row-label">Beds / Baths</div>
          <div className="ht-compare-cells">
            {homes.map(h => (
              <div key={h.id} className="ht-compare-cell">
                <span className="ht-compare-cell-val">{h.beds||"—"} / {h.baths||"—"}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Sqft */}
        <div className="ht-compare-row">
          <div className="ht-compare-row-label">Sq Ft</div>
          <div className="ht-compare-cells">
            {homes.map((h,i) => (
              <div key={h.id} className="ht-compare-cell">
                <span className={"ht-compare-cell-val" + (sqfts[i] === bestSqft ? " best" : "")}>
                  {h.sqft ? parseInt(h.sqft).toLocaleString() : "—"}
                </span>
                {sqfts[i] === bestSqft && h.sqft && <div className="ht-compare-winner">Most Space</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Price/sqft */}
        {ppsfs.some(p => p > 0) && (
          <div className="ht-compare-row">
            <div className="ht-compare-row-label">$/Sq Ft</div>
            <div className="ht-compare-cells">
              {homes.map((h,i) => (
                <div key={h.id} className="ht-compare-cell">
                  <span className={"ht-compare-cell-val" + (ppsfs[i] === bestPpsf && ppsfs[i] > 0 ? " best" : "")}>
                    {ppsfs[i] > 0 ? "$" + Math.round(ppsfs[i]) : "—"}
                  </span>
                  {ppsfs[i] === bestPpsf && ppsfs[i] > 0 && <div className="ht-compare-winner">Best Value</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rating */}
        <div className="ht-compare-row">
          <div className="ht-compare-row-label">Rating</div>
          <div className="ht-compare-cells">
            {homes.map((h,i) => (
              <div key={h.id} className="ht-compare-cell">
                <StarRating rating={h.rating||0} readonly size="small" />
                {ratings[i] === bestRating && bestRating > 0 && <div className="ht-compare-winner">Top Rated</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Notes */}
        {homes.some(h => h.notes) && (
          <div className="ht-compare-row">
            <div className="ht-compare-row-label">Notes</div>
            <div className="ht-compare-cells">
              {homes.map(h => (
                <div key={h.id} className="ht-compare-cell">
                  <span style={{ fontSize:11, color:"#666", lineHeight:1.5 }}>{h.notes || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// AUTH SCREEN
// ─────────────────────────────────────────────
function AuthScreen({ onAuth }) {
  const [mode, setMode] = useState("login"); // login | signup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // ── CATCH GOOGLE REDIRECT RESULT ON MOBILE ──
  // When Google redirects back to the app, the user lands on this screen.
  // We check immediately if there's a pending redirect result and log them in.
  useEffect(() => {
    setLoading(true);
    getRedirectResult(auth)
      .then((result) => {
        // If result is null, user didn't come from a redirect — just show login
        setLoading(false);
      })
      .catch((e) => {
        setError("Google sign-in failed. Please try again.");
        setLoading(false);
      });
  }, []);

  function friendlyError(code) {
    const map = {
      "auth/user-not-found": "No account found with that email.",
      "auth/wrong-password": "Incorrect password.",
      "auth/email-already-in-use": "An account with that email already exists.",
      "auth/weak-password": "Password must be at least 6 characters.",
      "auth/invalid-email": "Please enter a valid email address.",
      "auth/invalid-credential": "Incorrect email or password.",
      "auth/popup-closed-by-user": "Sign-in popup was closed.",
    };
    return map[code] || "Something went wrong. Please try again.";
  }

  async function handleEmailAuth() {
    if (!email || !password) return setError("Please enter your email and password.");
    setLoading(true); setError("");
    try {
      if (mode === "login") {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
      }
    } catch (e) {
      setError(friendlyError(e.code));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setLoading(true); setError("");
    try {
      // Try popup first — works on desktop and Android Chrome
      // iOS Safari will block this popup and throw, then we fall back to redirect
      await signInWithPopup(auth, googleProvider);
      setLoading(false);
    } catch (e) {
      if (e.code === "auth/popup-blocked" || e.code === "auth/popup-closed-by-user" || e.code === "auth/cancelled-popup-request") {
        // Popup was blocked (iOS Safari) — fall back to redirect
        try {
          await signInWithRedirect(auth, googleProvider);
        } catch (e2) {
          setError(friendlyError(e2.code));
          setLoading(false);
        }
      } else {
        setError(friendlyError(e.code));
        setLoading(false);
      }
    }
  }

  return loading ? (
    <div className="ht-loading">
      <div className="ht-loading-logo">HomeTrak<span>.</span></div>
      <div className="ht-spinner" />
    </div>
  ) : (
    <div className="ht-auth">
      <div className="ht-auth-hero">
        <div className="ht-auth-eyebrow">Find your new home.</div>
        <div className="ht-auth-headline">
          Home<em>Trak.</em>
        </div>
        <div className="ht-auth-sub">
          Save listings, track your search, and find the one.
        </div>
      </div>

      <div className="ht-auth-card">
        <div className="ht-auth-tabs">
          <button className={`ht-auth-tab${mode === "login" ? " active" : ""}`}
            onClick={() => { setMode("login"); setError(""); }}>
            Sign In
          </button>
          <button className={`ht-auth-tab${mode === "signup" ? " active" : ""}`}
            onClick={() => { setMode("signup"); setError(""); }}>
            Create Account
          </button>
        </div>

        {error && <div className="ht-auth-error">{error}</div>}

        <label className="ht-auth-label">Email</label>
        <input className="ht-auth-input" type="email" value={email}
          onChange={e => setEmail(e.target.value)} placeholder="you@example.com"
          onKeyDown={e => e.key === "Enter" && handleEmailAuth()} />

        <label className="ht-auth-label">Password</label>
        <input className="ht-auth-input" type="password" value={password}
          onChange={e => setPassword(e.target.value)} placeholder="••••••••"
          onKeyDown={e => e.key === "Enter" && handleEmailAuth()} />

        <button className="ht-auth-submit" onClick={handleEmailAuth} disabled={loading}>
          {loading ? "..." : mode === "login" ? "Sign In" : "Create Account"}
        </button>

        <div className="ht-auth-divider">
          <div className="ht-auth-divider-line" />
          <span className="ht-auth-divider-text">or</span>
          <div className="ht-auth-divider-line" />
        </div>

        <button className="ht-google-btn" onClick={handleGoogle} disabled={loading}>
          {/* Google G logo */}
          <svg className="ht-google-icon" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>
      </div>
    </div>
  );
} // end AuthScreen

// ─────────────────────────────────────────────
// FOOTER
// ─────────────────────────────────────────────
function Footer() {
  return (
    <div className="ht-footer">
      <div className="ht-footer-logo">HomeTrak<span>.</span></div>
      <div className="ht-footer-tagline">Ease and peace when choosing a home.</div>
      <hr className="ht-footer-divider" />
      <div className="ht-footer-row">
        <div>
          <div className="ht-footer-dev-label">Developer</div>
          <div className="ht-footer-dev-name">David Rojas</div>
          <a href="mailto:rojasdavidoficial@gmail.com" className="ht-footer-dev-email">rojasdavidoficial@gmail.com</a>
        </div>
        <div className="ht-footer-copy">
          <span className="ht-footer-year">HomeTrak© 2026</span><br />
          Trak Solutions<br />
          All rights reserved.
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────
export default function HomeTracker() {
  useGlobalStyles();

  const [user, setUser] = useState(undefined); // undefined = loading
  const [homes, setHomes] = useState([]);
  const [tab, setTab] = useState("dashboard");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("date");
  const [showSort, setShowSort] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [modalHome, setModalHome] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  const [showCompare, setShowCompare] = useState(false);

  // ── BACK BUTTON SUPPORT ──
  // Push a history entry whenever tab changes so the back button
  // navigates within the app instead of closing it.
  function navigateTo(newTab) {
    if (newTab !== tab) {
      window.history.pushState({ tab: newTab }, "", "");
    }
    setTab(newTab);
  }

  useEffect(() => {
    // Push initial state so there's always something to go back to
    window.history.replaceState({ tab: "dashboard" }, "", "");
  }, []);

  useEffect(() => {
    function handlePopState(e) {
      // If modal is open, close it first
      if (modalHome) { setModalHome(null); window.history.pushState({ tab }, "", ""); return; }
      // Otherwise go to the tab stored in history state
      const prevTab = e.state?.tab || "dashboard";
      setTab(prevTab);
      if (prevTab !== "add") { setEditId(null); setForm(EMPTY_FORM); }
    }
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [tab, modalHome]);

  // ── LISTEN TO AUTH STATE ──
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return unsub;
  }, []);

  // ── LISTEN TO FIRESTORE (real-time) ──
  useEffect(() => {
    if (!user) { setHomes([]); return; }
    const q = query(
      collection(db, "users", user.uid, "homes"),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setHomes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [user]);

  // Keep modal in sync when homes update
  useEffect(() => {
    if (modalHome) {
      const updated = homes.find(h => h.id === modalHome.id);
      setModalHome(updated || null);
    }
  }, [homes, modalHome]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── FIRESTORE ACTIONS ──
  async function addHome(data) {
    await addDoc(collection(db, "users", user.uid, "homes"), {
      ...data,
      createdAt: Date.now(),
      archived: false,
      photos: data.photos || [],
    });
  }

  async function updateHome(id, data) {
    await updateDoc(doc(db, "users", user.uid, "homes", id), data);
  }

  async function removeHome(id) {
    await deleteDoc(doc(db, "users", user.uid, "homes", id));
  }

  // ── HANDLERS ──
  function handleFormChange(e) { setForm(prev => ({ ...prev, [e.target.name]: e.target.value })); }
  function handleFormPhotos(photos) { setForm(prev => ({ ...prev, photos })); }

  async function handleSubmit() {
    if (!form.address.trim()) return alert("Please enter an address.");
    if (editId) {
      await updateHome(editId, { ...form });
      setEditId(null);
    } else {
      await addHome(form);
    }
    setForm(EMPTY_FORM);
    navigateTo("dashboard");
  }

  function startEdit(home) {
    setForm({
      address: home.address, price: home.price, beds: home.beds,
      baths: home.baths, sqft: home.sqft || "", link: home.link,
      status: home.status, notes: home.notes, photos: home.photos || [], rating: home.rating || 0,
    });
    setEditId(home.id);
    navigateTo("add");
  }

  function cancelEdit() { setForm(EMPTY_FORM); setEditId(null); navigateTo("dashboard"); }
  async function updateStatus(id, status) {
    if (id.endsWith("__rating")) {
      await updateHome(id.replace("__rating",""), { rating: status }); return;
    }
    await updateHome(id, { status });
  }
  async function updateRating(id, rating) { await updateHome(id, { rating }); }
  function toggleCompare(id) {
    setCompareIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id)
      : prev.length >= 3 ? prev
      : [...prev, id]
    );
  }
  async function toggleArchive(id) {
    const home = homes.find(h => h.id === id);
    if (home) await updateHome(id, { archived: !home.archived });
  }
  async function updatePhotos(id, photos) { await updateHome(id, { photos }); }
  async function deleteHome(id) {
    if (!window.confirm("Permanently delete this home?")) return;
    await removeHome(id);
  }
  async function handleSignOut() {
    await signOut(auth);
    setShowUserMenu(false);
  }

  // ── DERIVED ──
  const activeHomes = homes.filter(h => !h.archived);
  const archivedHomes = homes.filter(h => h.archived);

  const SORT_OPTIONS = [
    { value: "date",       label: "Date Added" },
    { value: "price_asc",  label: "Price: Low → High" },
    { value: "price_desc", label: "Price: High → Low" },
    { value: "rating",     label: "Your Rating" },
    { value: "sqft",       label: "Square Feet" },
  ];

  const filteredHomes = activeHomes
    .filter(h => filter === "all" || h.status === filter)
    .filter(h => !search.trim() || h.address.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const n = (v) => parseFloat(String(v||"0").replace(/[^0-9.]/g,""))||0;
      if (sort === "price_asc")  return n(a.price) - n(b.price);
      if (sort === "price_desc") return n(b.price) - n(a.price);
      if (sort === "rating")     return (b.rating||0) - (a.rating||0);
      if (sort === "sqft")       return n(b.sqft) - n(a.sqft);
      return (b.createdAt||0) - (a.createdAt||0);
    });
  const avgPrice = (() => {
    const prices = activeHomes
      .map(h => parseFloat(String(h.price).replace(/[^0-9.]/g, "")))
      .filter(n => !isNaN(n) && n > 0);
    if (!prices.length) return null;
    const avg = prices.reduce((a, b) => a + b, 0) / prices.length;
    return avg >= 1000000 ? "$" + (avg / 1000000).toFixed(1) + "M" : "$" + Math.round(avg / 1000) + "K";
  })();

  const userInitial = user?.displayName?.[0] || user?.email?.[0]?.toUpperCase() || "?";

  // ── RENDER ──
  if (user === undefined) {
    return (
      <div className="ht-loading">
        <div className="ht-loading-logo">HomeTrak<span>.</span></div>
        <div className="ht-spinner" />
      </div>
    );
  }

  if (!user) return <AuthScreen />;

  return (
    <div className="ht-root">
      {showCompare && (
        <CompareView
          homes={homes.filter(h => compareIds.includes(h.id))}
          onClose={() => setShowCompare(false)}
        />
      )}

      {modalHome && (
        <ListingModal home={modalHome} onClose={() => setModalHome(null)}
          onEdit={startEdit} onArchive={toggleArchive} onDelete={deleteHome}
          onStatusChange={updateStatus} onPhotosChange={updatePhotos} />
      )}

      {compareMode && compareIds.length > 0 && (
        <div className="ht-compare-bar">
          <div className="ht-compare-bar-left">
            <span className="ht-compare-bar-label">Compare</span>
            <div className="ht-compare-bar-homes">
              {compareIds.map(id => {
                const h = homes.find(x => x.id === id);
                return h ? <span key={id} className="ht-compare-bar-chip">{h.address.split(",")[0]}</span> : null;
              })}
            </div>
          </div>
          {compareIds.length >= 2 && (
            <button className="ht-compare-btn" onClick={() => setShowCompare(true)}>
              Compare {compareIds.length}
            </button>
          )}
          <button className="ht-compare-clear" onClick={() => { setCompareIds([]); setCompareMode(false); }}>✕</button>
        </div>
      )}

      {/* User menu */}
      {showUserMenu && (
        <>
          <div className="ht-user-menu-overlay" onClick={() => setShowUserMenu(false)} />
          <div className="ht-user-menu">
            <div className="ht-user-menu-info">
              <div className="ht-user-menu-name">{user.displayName || "My Account"}</div>
              <div className="ht-user-menu-email">{user.email}</div>
            </div>
            <button className="ht-user-menu-btn" onClick={handleSignOut}>Sign Out</button>
          </div>
        </>
      )}

      {/* Header */}
      <div className="ht-header">
        <div className="ht-logo">HomeTrak<span>.</span></div>
        <div className="ht-header-right">
          <button className="ht-header-add-btn" onClick={() => navigateTo("add")}>+ Add Home</button>
          <div className="ht-avatar" onClick={() => setShowUserMenu(v => !v)}>
            {user.photoURL
              ? <img src={user.photoURL} alt="" />
              : userInitial}
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="ht-nav">
        {[
          { id: "dashboard", label: "Homes", count: activeHomes.length },
          { id: "add",       label: editId ? "Edit" : "Add" },
          { id: "archived",  label: "Archive", count: archivedHomes.length },
        ].map(n => (
          <button key={n.id}
            className={`ht-nav-btn${tab === n.id ? " active" : ""}`}
            onClick={() => { if (n.id !== "add") { setEditId(null); setForm(EMPTY_FORM); } navigateTo(n.id); }}>
            {n.label}{n.count !== undefined ? ` · ${n.count}` : ""}
          </button>
        ))}
      </nav>

      <div className="ht-content">

        {tab === "dashboard" && (
          <div>
            <div className="ht-hero">
              <div className="ht-hero-eyebrow">Find your new home.</div>
              <div className="ht-hero-headline">Your hunt,<br /><em>organized.</em></div>
              {activeHomes.length === 0 ? (
                <>
                  <div className="ht-hero-sub">Save listings from Zillow, Redfin, your agent — all in one place.</div>
                  <button className="ht-hero-cta" onClick={() => navigateTo("add")}>
                    Add your first home <span className="ht-hero-cta-arrow">→</span>
                  </button>
                </>
              ) : (
                <div className="ht-hero-stats">
                  <div>
                    <span className="ht-hero-stat-num">{activeHomes.length}</span>
                    <span className="ht-hero-stat-lbl">Saved</span>
                  </div>
                  {avgPrice && <div>
                    <span className="ht-hero-stat-num">{avgPrice}</span>
                    <span className="ht-hero-stat-lbl">Avg Price</span>
                  </div>}
                  <div>
                    <span className="ht-hero-stat-num">{activeHomes.filter(h => h.status === "visited").length}</span>
                    <span className="ht-hero-stat-lbl">Visited</span>
                  </div>
                  <div>
                    <span className="ht-hero-stat-num">{activeHomes.filter(h => h.status === "offer_made").length}</span>
                    <span className="ht-hero-stat-lbl">Offers</span>
                  </div>
                </div>
              )}
            </div>

            {activeHomes.length > 1 && (
              <div className="ht-filters" style={{ marginTop: 16 }}>
                <button className={`ht-filter${filter === "all" ? " active" : ""}`} onClick={() => setFilter("all")}>All</button>
                {STATUSES.map(s => (
                  <button key={s.value} className={`ht-filter${filter === s.value ? " active" : ""}`} onClick={() => setFilter(s.value)}>
                    {s.label}
                  </button>
                ))}
              </div>
            )}

            {/* Toolbar */}
            {activeHomes.length > 0 && (
              <div className="ht-toolbar">
                <div className="ht-search-wrap">
                  <span className="ht-search-icon">🔍</span>
                  <input className="ht-search" placeholder="Search by address..."
                    value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <div className="ht-sort-wrap">
                  <button className={"ht-sort-btn" + (sort !== "date" ? " active" : "")}
                    onClick={() => setShowSort(v => !v)}>
                    ↕ Sort
                  </button>
                  {showSort && (
                    <div className="ht-sort-menu">
                      {SORT_OPTIONS.map(o => (
                        <button key={o.value}
                          className={"ht-sort-option" + (sort === o.value ? " active" : "")}
                          onClick={() => { setSort(o.value); setShowSort(false); }}>
                          {o.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button className={"ht-sort-btn" + (compareMode ? " active" : "")}
                  onClick={() => { setCompareMode(v => !v); if (compareMode) setCompareIds([]); }}>
                  ⊞ Compare
                </button>
              </div>
            )}

            {filteredHomes.length === 0 ? (
              <div className="ht-empty" style={{ paddingTop: 48 }}>
                <div className="ht-empty-num">0</div>
                <div className="ht-empty-title">{activeHomes.length === 0 ? "No homes yet" : "Nothing here"}</div>
                <div className="ht-empty-sub">
                  {activeHomes.length === 0 ? 'Hit "Add Home" to get started.' : "No results — try a different search or filter."}
                </div>
              </div>
            ) : (
              <div>
                <div className="ht-section-row">
                  <span className="ht-section-title">Listings</span>
                  <span className="ht-section-count">
                    {filteredHomes.length}{filter !== "all" ? ` · ${STATUS_MAP[filter]?.label}` : ""}{search ? ` · "${search}"` : ""}
                  </span>
                </div>
                <div className="ht-listings-grid">
                  {filteredHomes.map((h, i) => (
                    <ListingCard key={h.id} home={h} index={i}
                      onClick={() => setModalHome(h)}
                      compareMode={compareMode}
                      isCompared={compareIds.includes(h.id)}
                      onToggleCompare={toggleCompare}
                      onRate={updateRating}
                    />
                  ))}
                </div>
              </div>
            )}
            <Footer />
          </div>
        )}

        {tab === "add" && (
          <HomeForm form={form} onChange={handleFormChange}
            onPhotosChange={handleFormPhotos}
            onSubmit={handleSubmit} onCancel={cancelEdit} isEdit={!!editId} />
        )}

        {tab === "archived" && (
          <div style={{ paddingTop: 20 }}>
            {archivedHomes.length === 0 ? (
              <div className="ht-empty">
                <div className="ht-empty-num">∅</div>
                <div className="ht-empty-title">Nothing archived</div>
                <div className="ht-empty-sub">Archive homes that are sold or no longer relevant.</div>
              </div>
            ) : (
              <div>
                <div className="ht-archive-banner">
                  <div className="ht-archive-num">{archivedHomes.length}</div>
                  <div className="ht-archive-text">
                    Archived homes — out of sight, not deleted.<br />Tap any card to restore it.
                  </div>
                </div>
                <div className="ht-section-row">
                  <span className="ht-section-title">Archived</span>
                  <span className="ht-section-count">{archivedHomes.length}</span>
                </div>
                <div className="ht-listings-grid">
                  {archivedHomes.map((h, i) => (
                    <ListingCard key={h.id} home={h} index={i} onClick={() => setModalHome(h)} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}